import React, { useState } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import { useQuery, useQueryClient } from 'react-query';

import Button from '../../components/Button';
import ToastNotification from '../../components/ToastNotification';
import DefaultModal from '../../components/modals/DefaultModal';
import { usePermissions } from '../../hooks/usePermissions';
import { useOperatorToken } from '../../hooks/useOperatorToken';
import { cancelTicket, getCancelPreview, settleTicketRefund } from './api';
import {
  APPROVAL_REASON_MAX_LENGTH,
  APPROVAL_REASON_MIN_LENGTH,
  BookCancelFailure,
  canOfferBookCancel,
  cancelDialogSubtitle,
  cancellationCreditsText,
  classifyBookCancelFailure,
  formatDate,
  isDebitRefundOption,
  isLedgerRefundPending,
  isOperatorCancelledTicket,
  mayHaveRefundToSettle,
  returnableRefundOption,
  settleDebitOption,
  validateApprovalReason,
} from './helpers';
import { formatMessage, printShopStrings } from './strings';
import {
  BookCancelPreview,
  BookRefundOption,
  CancelTicketPayload,
  TicketDetail,
} from './types';

/**
 * The operator cancel of a live book order, and the later "Return credits"
 * (book-order operator cancel). Both are role-gated: the FusionAuth role
 * `print_shop_cancel`, plus `credits_adjust` to return credits. DS checks the
 * roles again from the operator's id token, sent on each request.
 *
 * Cancelling is rare, so the UI is sized to it: one note, at most one
 * checkbox, and an informational printer line when it applies (nothing is
 * asked about the printer: the print team talks to them). Anything rarer (a
 * typed-in credit amount) is engineering's, through the API.
 */

const STRINGS = printShopStrings.detail.cancel;

const CANCEL_PREVIEW_QUERY_KEY = 'print-shop-cancel-preview';

/** The queries a cancel or a settle makes stale. */
const useInvalidateTicket = (orderId: string, ticketQueryKey: unknown[]) => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries(ticketQueryKey);
    queryClient.invalidateQueries('print-shop-tickets');
    queryClient.invalidateQueries('print-shop-ticket-counts');
    queryClient.invalidateQueries([CANCEL_PREVIEW_QUERY_KEY, orderId]);
  };
};

/** The order number when there is one, else the ticket id. */
const orderNumberOf = (ticket: TicketDetail): string =>
  ticket.afyOrderNumber ?? ticket.orderId;

/** One red line: why the order cannot be cancelled, or a refund flag. */
const DangerLine = ({
  testId,
  children,
}: {
  testId: string;
  children: React.ReactNode;
}) => (
  <p
    data-testid={testId}
    className="flex items-start gap-2 rounded-lg border border-error-100 bg-error-50 px-3 py-2.5 text-[13px] leading-[18px] text-error-700"
  >
    <FiAlertTriangle aria-hidden className="mt-px h-4 w-4 flex-none" />
    <span>{children}</span>
  </p>
);

/** The inline failure of a dialog that stays open, with "Sign in again". */
const FailureAlert = ({
  failure,
  onSignIn,
}: {
  failure: BookCancelFailure;
  onSignIn: () => void;
}) => (
  <div
    role="alert"
    data-testid="book-cancel-feedback"
    data-kind={failure.kind}
    className="flex flex-col items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800"
  >
    <span>{failure.message}</span>
    {(failure.kind === 'unverified' || failure.kind === 'forbidden') && (
      <Button variant="DARK" size="xs" onClick={onSignIn}>
        {STRINGS.signInAgain}
      </Button>
    )}
  </div>
);

const FlagLines = ({ flags }: { flags: BookRefundOption['flags'] }) => (
  <>
    {flags.map((flag) => (
      <DangerLine key={flag} testId={`book-cancel-flag-${flag}`}>
        {STRINGS.flags[flag] ?? flag}
      </DangerLine>
    ))}
  </>
);

/** The cancel body for what the operator chose, against the loaded plan. */
const buildCancelPayload = (
  preview: BookCancelPreview,
  note: string,
  returned: BookRefundOption | null,
): CancelTicketPayload => {
  const payload: CancelTicketPayload = {
    reason: note.trim(),
    refundMode: returned ? returned.mode : 'none',
    // Not asked of the operator: an older DS still needs it (see the type).
    printerNoticeAcknowledged: preview.printerAlreadyNotified,
  };
  if (returned && isDebitRefundOption(returned)) {
    payload.amountSource = 'debit';
    payload.amount = returned.amount;
  }
  return payload;
};

/**
 * "Cancel order" on a book ticket: the hero link and its dialog. The link
 * shows only with the role, on a live (not imported) book order in any status,
 * Shipped included. The plan (refund choices, printer state) comes from the DS
 * preview when the dialog opens.
 */
export const BookCancelAction = ({
  ticket,
  ticketQueryKey,
  onCancelled,
}: {
  ticket: TicketDetail;
  ticketQueryKey: unknown[];
  /** Called with the success message, for the page banner. */
  onCancelled: (message: string) => void;
}) => {
  const { canCancelBookOrders, canAdjustCredits } = usePermissions();
  const { getOperatorToken, signInAgain } = useOperatorToken();
  const invalidate = useInvalidateTicket(ticket.orderId, ticketQueryKey);

  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [noteTouched, setNoteTouched] = useState(false);
  const [returnCredits, setReturnCredits] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<BookCancelFailure | null>(null);

  const previewQuery = useQuery<BookCancelPreview>(
    [CANCEL_PREVIEW_QUERY_KEY, ticket.orderId, 'cancel'],
    async () =>
      getCancelPreview(ticket.orderId, { token: await getOperatorToken() }),
    {
      enabled: open,
      retry: false,
      cacheTime: 0,
      refetchOnWindowFocus: false,
    },
  );

  if (!canCancelBookOrders() || !canOfferBookCancel(ticket)) {
    return null;
  }

  const orderNumber = orderNumberOf(ticket);

  const reset = () => {
    setNote('');
    setNoteTouched(false);
    setReturnCredits(false);
    setFailure(null);
  };

  const close = () => {
    if (submitting) return;
    setOpen(false);
    reset();
  };

  const preview = previewQuery.data;
  const options = preview?.refundOptions ?? [];
  const returnable = returnableRefundOption(options, canAdjustCredits());
  const returned = returnCredits ? returnable : null;
  // Credits were charged but cannot be returned from the dialog: say so, so
  // the operator knows to ask engineering.
  const cannotReturnCredits =
    !returnable &&
    (preview?.refundBlockedReason === 'debit_owner_mismatch' ||
      options.some((option) => option.mode === 'ledger'));
  // The option that is sent, for its flags: the returned one, else `none`.
  const chosen = returned ?? options.find((option) => option.mode === 'none');
  const noteError = validateApprovalReason(note);
  const noteBounds = {
    min: String(APPROVAL_REASON_MIN_LENGTH),
    max: String(APPROVAL_REASON_MAX_LENGTH),
  };
  const canConfirm =
    Boolean(preview?.eligible) && noteError === null && !submitting;

  const confirm = async () => {
    if (!preview || !canConfirm) return;
    const payload = buildCancelPayload(preview, note, returned);
    setSubmitting(true);
    setFailure(null);
    try {
      // Read (after a refresh) at Confirm, for this one request only.
      const token = await getOperatorToken();
      await cancelTicket(ticket.orderId, payload, { token });
      const message = formatMessage(STRINGS.success, { orderNumber });
      invalidate();
      setOpen(false);
      reset();
      ToastNotification({ type: 'success', message });
      onCancelled(message);
    } catch (error) {
      const result = classifyBookCancelFailure(
        error,
        formatMessage(STRINGS.error, { orderNumber }),
      );
      if (result.close) {
        ToastNotification({ type: 'failure', message: result.message });
        invalidate();
        setOpen(false);
        reset();
      } else {
        setFailure(result);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const previewFailure = previewQuery.error
    ? classifyBookCancelFailure(previewQuery.error, STRINGS.previewError)
    : null;
  const subtitle = preview ? cancelDialogSubtitle(preview) : '';

  return (
    <>
      <button
        type="button"
        data-testid="book-cancel-button"
        onClick={() => {
          reset();
          setOpen(true);
        }}
        className="text-xs font-semibold text-white/80 underline transition hover:text-white"
      >
        {STRINGS.button}
      </button>

      <DefaultModal
        title={formatMessage(STRINGS.dialogTitle, {
          orderNumber: preview?.afyOrderNumber ?? orderNumber,
        })}
        showModal={open}
        setShowModal={(show) => {
          if (!show) close();
        }}
        size="md"
      >
        {open && (
          <div className="flex flex-col gap-4" data-testid="book-cancel-dialog">
            {previewQuery.isLoading && (
              <p className="text-sm text-neutral-500">{STRINGS.loadingPlan}</p>
            )}
            {previewFailure && (
              <FailureAlert failure={previewFailure} onSignIn={signInAgain} />
            )}

            {preview && (
              <>
                {subtitle && (
                  <p
                    data-testid="book-cancel-subtitle"
                    className="break-all text-sm text-neutral-600"
                  >
                    {subtitle}
                  </p>
                )}

                {!preview.eligible ? (
                  preview.blockedReason && (
                    <DangerLine testId="book-cancel-blocked">
                      {STRINGS.blocked[preview.blockedReason] ??
                        STRINGS.blockedFallback}
                    </DangerLine>
                  )
                ) : (
                  <>
                    <div className="flex flex-col gap-1">
                      <label
                        htmlFor="book-cancel-note"
                        className="text-sm font-semibold text-neutral-800"
                      >
                        {STRINGS.noteLabel}
                      </label>
                      <input
                        id="book-cancel-note"
                        data-testid="book-cancel-note"
                        type="text"
                        value={note}
                        placeholder={STRINGS.notePlaceholder}
                        aria-invalid={noteTouched && noteError !== null}
                        onChange={(event) => setNote(event.target.value)}
                        onBlur={() => setNoteTouched(true)}
                        className="rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                      {noteTouched && noteError && (
                        <span
                          data-testid="book-cancel-note-error"
                          className="text-xs text-red-600"
                        >
                          {formatMessage(
                            noteError === 'too_short'
                              ? STRINGS.noteTooShort
                              : STRINGS.noteTooLong,
                            noteBounds,
                          )}
                        </span>
                      )}
                    </div>

                    {returnable && (
                      <label className="flex min-h-[32px] items-center gap-2 text-sm text-neutral-800">
                        <input
                          type="checkbox"
                          data-testid="book-cancel-return-credits"
                          checked={returnCredits}
                          onChange={(event) =>
                            setReturnCredits(event.target.checked)
                          }
                          className="h-[18px] w-[18px]"
                        />
                        <span>{STRINGS.returnCredits}</span>
                      </label>
                    )}

                    {cannotReturnCredits && (
                      <p
                        data-testid="book-cancel-no-return"
                        className="text-sm text-neutral-500"
                      >
                        {STRINGS.noReturnCredits}
                      </p>
                    )}

                    {chosen && <FlagLines flags={chosen.flags} />}

                    {preview.printerAlreadyNotified && (
                      <p
                        data-testid="book-cancel-printer-notice"
                        className="text-sm text-neutral-600"
                      >
                        {STRINGS.printerNotice}
                      </p>
                    )}
                  </>
                )}
              </>
            )}

            {failure && <FailureAlert failure={failure} onSignIn={signInAgain} />}

            <div className="flex justify-end gap-2">
              <Button variant="OUTLINED" disabled={submitting} onClick={close}>
                {STRINGS.keepOrder}
              </Button>
              {preview?.eligible && (
                <Button
                  variant="DANGER"
                  data-testid="book-cancel-confirm"
                  isProcessing={submitting}
                  disabled={!canConfirm}
                  onClick={confirm}
                >
                  {STRINGS.confirm}
                </Button>
              )}
            </div>
          </div>
        )}
      </DefaultModal>
    </>
  );
};

/**
 * The archived view of an operator-cancelled ticket: one line with the
 * reason, the credits outcome, who and when, a second line only when the
 * refund needs engineering, plus "Return credits" when the settle preview
 * offers the order's debit. Rendered inside the hero, so it is white on teal.
 */
export const BookCancellationSummary = ({
  ticket,
  ticketQueryKey,
  onSettled,
}: {
  ticket: TicketDetail;
  ticketQueryKey: unknown[];
  /** Called with the success message, for the page banner. */
  onSettled: (message: string) => void;
}) => {
  const { canCancelBookOrders, canAdjustCredits } = usePermissions();
  const { getOperatorToken, signInAgain } = useOperatorToken();
  const invalidate = useInvalidateTicket(ticket.orderId, ticketQueryKey);

  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<BookCancelFailure | null>(null);

  const mayReturnCredits =
    mayHaveRefundToSettle(ticket) && canCancelBookOrders() && canAdjustCredits();

  const previewQuery = useQuery<BookCancelPreview>(
    [CANCEL_PREVIEW_QUERY_KEY, ticket.orderId, 'settle'],
    async () =>
      getCancelPreview(ticket.orderId, { token: await getOperatorToken() }),
    {
      enabled: mayReturnCredits,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const { cancellation } = ticket;
  if (!cancellation || !isOperatorCancelledTicket(ticket)) {
    return null;
  }

  const orderNumber = orderNumberOf(ticket);
  // Only the order's own debit is returned here; a typed-in amount is
  // engineering's, through the API.
  const option = mayReturnCredits ? settleDebitOption(previewQuery.data) : null;

  const close = () => {
    if (submitting) return;
    setOpen(false);
    setFailure(null);
  };

  const confirm = async () => {
    if (!option || submitting) return;
    setSubmitting(true);
    setFailure(null);
    try {
      const token = await getOperatorToken();
      await settleTicketRefund(
        ticket.orderId,
        {
          amountSource: 'debit',
          amount: option.amount,
          noHubspotRestoreConfirmed: true,
        },
        { token },
      );
      const message = formatMessage(STRINGS.settle.success, { orderNumber });
      invalidate();
      setOpen(false);
      ToastNotification({ type: 'success', message });
      onSettled(message);
    } catch (error) {
      const result = classifyBookCancelFailure(
        error,
        formatMessage(STRINGS.settle.error, { orderNumber }),
      );
      if (result.close) {
        ToastNotification({ type: 'failure', message: result.message });
        invalidate();
        setOpen(false);
      } else {
        setFailure(result);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const flags = cancellation.refund?.flags ?? [];

  return (
    <div data-testid="book-cancellation-summary" className="flex flex-col gap-1">
      <p className="font-semibold">{STRINGS.banner.title}</p>
      {/* The reason is operator-typed: rendered as text, never as markup. */}
      <p
        data-testid="book-cancellation-line"
        className="whitespace-pre-wrap break-words"
      >
        {formatMessage(STRINGS.banner.line, {
          reason: cancellation.reason,
          credits: cancellationCreditsText(cancellation),
          by: cancellation.by?.id ?? '—',
          date: formatDate(cancellation.at),
        })}
      </p>
      {isLedgerRefundPending(cancellation) && (
        <p data-testid="book-cancellation-ledger-pending">
          {STRINGS.banner.ledgerPending}
        </p>
      )}
      {flags.map((flag) => (
        <p key={flag} data-testid={`book-cancellation-flag-${flag}`}>
          {STRINGS.flags[flag as keyof typeof STRINGS.flags] ?? flag}
        </p>
      ))}
      {option && (
        <div className="mt-2">
          <button
            type="button"
            data-testid="book-return-credits-button"
            onClick={() => {
              setFailure(null);
              setOpen(true);
            }}
            className="inline-flex items-center rounded-lg bg-white px-4 py-2 text-sm font-bold text-primary-700 shadow-sm transition hover:bg-primary-50"
          >
            {STRINGS.settle.button}
          </button>
        </div>
      )}

      <DefaultModal
        title={
          option
            ? formatMessage(STRINGS.settle.dialogTitle, {
                amount: String(option.amount),
              })
            : undefined
        }
        showModal={open}
        setShowModal={(show) => {
          if (!show) close();
        }}
        size="md"
      >
        {open && option && (
          <div className="flex flex-col gap-4" data-testid="book-settle-dialog">
            <p className="text-sm text-neutral-600">{STRINGS.settle.intro}</p>

            {failure && <FailureAlert failure={failure} onSignIn={signInAgain} />}

            <div className="flex justify-end gap-2">
              <Button variant="OUTLINED" disabled={submitting} onClick={close}>
                {printShopStrings.detail.actions.cancelButton}
              </Button>
              <Button
                variant="PRIMARY"
                data-testid="book-settle-confirm"
                isProcessing={submitting}
                disabled={submitting}
                onClick={confirm}
              >
                {STRINGS.settle.confirm}
              </Button>
            </div>
          </div>
        )}
      </DefaultModal>
    </div>
  );
};
